import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { LocalizedLink } from 'src/components/common/LocalizedLink';
import {
  getSanctumCsrfCookie,
  getXsrfToken,
  postContactGeneralSubmit,
} from '@/API/apiService';
import { PhoneCodeSelect } from '@/components/monthly/ApplyForms/PhoneCode/PhoneCodeSelect';
import { useLocation } from 'react-router-dom';

type FormValues = {
  nameKanji: string; // 任意
  nameRoma: string; // 必須（BE: name_roma）
  email: string; // 必須
  phone: string; // 任意
  category: string; // 必須
  inquiry: string; // 必須（BE: message）
  agreement: boolean; // チェック必須（送信前の検証用）
  language: string; // 必須（仮置き: 'ja'）
};

export const ContactIndex: React.FC = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    handleSubmit: handleFormSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      nameKanji: '',
      nameRoma: '',
      email: '',
      phone: '',
      category: '',
      inquiry: '',
      agreement: false,
      language: 'ja', // 仮置き。将来的に適切な値に変更予定
    },
    mode: 'onChange',
  });
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const [step, setStep] = useState<number>(1);

  // metaタグ（タイトル「NEWS」）用の変数
  const meta = (
    <Helmet>
      <title>NEWS</title>
      <meta name="description" content="NEWS Page Meta Description" />
    </Helmet>
  );

  // API 送信用関数
  const submitContact = async (data: FormValues) => {
    // 1. CSRF Cookie を取得
    await getSanctumCsrfCookie();

    // 2. クッキーから XSRF トークンを取得
    const xsrfToken = getXsrfToken();

    // 3. BE が期待するキーに整形
    const payload = {
      name_kanji: data.nameKanji,
      name_roma: data.nameRoma,
      email: data.email,
      message: data.inquiry, // inquiry を message にマッピング
      phone: data.phone,
      category: data.category,
      language: data.language, // 今は 'ja' 固定
    };

    try {
      const response = await postContactGeneralSubmit(payload);
      console.log('送信成功:', response.data);
      setStep(3);
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  // onSubmit ハンドラー（ステップによる動作切替）
  const onSubmit = async (data: FormValues) => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      await submitContact(data);
    }
  };

  const handleBackToInput = () => {
    setStep(1);
  };

  // 同意チェックの監視
  const agreementChecked = watch('agreement');

  const categoryList = useEnglish
    ? [
        { label: 'Hotel', value: 'hotel' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Corporation', value: 'corporation' },
        { label: 'Other', value: 'other' },
      ]
    : [
        { label: '予約・宿泊について', value: 'hotel' },
        { label: 'マンスリー契約について', value: 'monthly' },
        { label: '法人利用について', value: 'corporation' },
        { label: 'その他お問い合わせ', value: 'other' },
      ];
  const getLabelFromValue = (value: string) => {
    const found = categoryList.find((item) => item.value === value);
    return found ? found.label : null;
  };

  // ========================
  // 1. 入力画面
  // ========================
  if (step === 1) {
    return (
      <>
        {meta}
        <div className="min-h-screen bg-whiteYellow py-10 px-4 md:py-20">
          <div className="max-w-[700px] mx-auto my-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {useEnglish ? 'Inquiry' : 'お問い合わせ'}
            </h1>
            <div className="flex justify-between items-center mb-8 text-gray-700">
              <p>
                {useEnglish
                  ? 'Frequently asked questions ('
                  : 'よくあるご質問は、'}
                <a
                  href="https://judicious-sodium-d58.notion.site/theapartmenthotel?pvs=4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline mx-1"
                >
                  {useEnglish ? 'FAQ' : 'こちら'}
                </a>
                {useEnglish ? ') are summarized here.' : 'にまとめております。'}
              </p>
            </div>
            <p className="text-xs text-right">
              {useEnglish
                ? 'Fields marked with * are required.'
                : '＊は入力必須項目です'}
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* お名前(漢字) */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    {useEnglish ? 'Name (Kanji)' : 'お名前(漢字)'}
                  </label>
                </div>
                <div className="md:w-9/12">
                  <input
                    {...register('nameKanji')}
                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none focus:border-black"
                    placeholder={useEnglish ? 'e.g. 山田 太郎' : '例）山田太郎'}
                  />
                </div>
              </div>
              {/* お名前(ローマ字)* */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    {useEnglish ? 'Name (Romaji)' : 'お名前(ローマ字)'}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
                <div className="md:w-9/12">
                  <input
                    {...register('nameRoma', {
                      required: useEnglish
                        ? 'Name (Romaji) is required.'
                        : 'お名前(ローマ字)は必須です',
                    })}
                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none focus:border-black"
                    placeholder={
                      useEnglish ? 'e.g. yamada_taro' : '例）yamada_taro'
                    }
                  />
                  {errors.nameRoma && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.nameRoma.message}
                    </p>
                  )}
                </div>
              </div>
              {/* E-mail* */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    E-mail<span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
                <div className="md:w-9/12">
                  <input
                    type="email"
                    {...register('email', {
                      required: useEnglish
                        ? 'E-mail is required.'
                        : 'メールアドレスは必須です',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: useEnglish
                          ? 'E-mail is invalid.'
                          : 'メールアドレスの形式が不正です',
                      },
                    })}
                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none focus:border-black"
                    placeholder="example@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
              {/* 電話番号 */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    {useEnglish ? 'Phone Number' : '電話番号'}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
                <div className="md:w-9/12">
                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: useEnglish
                        ? 'Phone Number is required.'
                        : '電話番号は必須です',
                    }}
                    render={({ field }) => <PhoneCodeSelect {...field} />}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              </div>
              {/* カテゴリ */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    {useEnglish ? 'Category' : 'カテゴリ'}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
                <div className="md:w-9/12">
                  <select
                    className="w-full border border-gray-400 rounded px-2 py-2 focus:outline-none focus:border-black"
                    {...register('category', {
                      required: useEnglish
                        ? 'Category is required.'
                        : 'カテゴリは必須です',
                    })}
                  >
                    {categoryList.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.category.message}
                    </p>
                  )}
                </div>
              </div>
              {/* お問い合わせ内容* */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12 md:pr-2">
                  <label className="block font-medium text-gray-800">
                    {useEnglish ? 'Inquiry Details' : 'お問い合わせ内容'}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
                <div className="md:w-9/12">
                  <textarea
                    {...register('inquiry', {
                      required: useEnglish
                        ? 'Inquiry Details is required.'
                        : 'お問い合わせ内容は必須です',
                    })}
                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none focus:border-black"
                    rows={5}
                    placeholder={
                      useEnglish
                        ? 'Please enter the details of your inquiry.'
                        : 'お問い合わせ内容をご記入ください'
                    }
                  />
                  {errors.inquiry && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.inquiry.message}
                    </p>
                  )}
                </div>
              </div>
              {/* 同意チェック */}
              <div className="flex flex-col md:flex-row mb-4">
                <div className="md:w-3/12" />
                <div className="md:w-9/12 flex flex-col items-center">
                  <div className="mb-6 flex items-center">
                    <input
                      type="checkbox"
                      {...register('agreement', { required: true })}
                      className="mr-2 w-4 h-4 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-700">
                      {useEnglish ? 'By submitting, you agree to our ' : ''}
                      <LocalizedLink
                        to="/policy"
                        className="text-blue-600 underline"
                      >
                        {useEnglish ? 'privacy policy' : '個人情報の取り扱い'}
                      </LocalizedLink>
                      {useEnglish
                        ? ' regarding personal information.'
                        : 'について、同意の上送信します。'}
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={!isValid || !agreementChecked}
                    className={`py-2 px-8 rounded transition-colors ${
                      !isValid || !agreementChecked
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    {useEnglish ? 'Proceed to Confirmation' : '確認画面に進む'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ========================
  // 2. 確認画面
  // ========================
  if (step === 2) {
    const nameKanji = watch('nameKanji');
    const nameRoma = watch('nameRoma');
    const email = watch('email');
    const phone = watch('phone');
    const category = watch('category');
    const inquiry = watch('inquiry');

    return (
      <>
        {meta}
        <div className="min-h-screen bg-whiteYellow py-10 px-4 md:py-20">
          <div className="max-w-[700px] mx-auto my-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {useEnglish ? 'Inquiry' : 'お問い合わせ'}
            </h1>
            <p className="mb-8 text-gray-700">
              {useEnglish
                ? 'Frequently asked questions ('
                : 'よくあるご質問は、'}
              <a
                href="https://judicious-sodium-d58.notion.site/theapartmenthotel?pvs=4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline mx-1"
              >
                {useEnglish ? 'FAQ' : 'こちら'}
              </a>
              {useEnglish ? ') are summarized here.' : 'にまとめております。'}
            </p>
            <div className="space-y-4 mb-8 text-gray-800">
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">
                  {useEnglish ? 'Name (Kanji)' : 'お名前(漢字)'}:
                </strong>
                <span className="md:w-9/12">{nameKanji}</span>
              </div>
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">
                  {useEnglish ? 'Name (Romaji)' : 'お名前(ローマ字)'}:
                </strong>
                <span className="md:w-9/12">{nameRoma}</span>
              </div>
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">E-mail:</strong>
                <span className="md:w-9/12">{email}</span>
              </div>
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">
                  {useEnglish ? 'Phone Number' : '電話番号'}:
                </strong>
                <span className="md:w-9/12">{phone}</span>
              </div>
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">
                  {useEnglish ? 'Category' : 'カテゴリ'}:
                </strong>
                <span className="md:w-9/12">{getLabelFromValue(category)}</span>
              </div>
              <div className="flex flex-col md:flex-row">
                <strong className="md:w-3/12">
                  {useEnglish ? 'Inquiry Details' : 'お問い合わせ内容'}:
                </strong>
                <span className="md:w-9/12 whitespace-pre-line">{inquiry}</span>
              </div>
            </div>
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleBackToInput}
                className="bg-gray-500 text-white py-3 px-6 rounded hover:bg-gray-600 transition-colors"
              >
                {useEnglish ? 'Back' : '入力画面に戻る'}
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                className="bg-black text-white py-3 px-6 rounded hover:bg-gray-800 transition-colors"
              >
                {useEnglish ? 'Submit' : '送信する'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ========================
  // 3. 完了画面
  // ========================
  if (step === 3) {
    return (
      <>
        {meta}
        <div className="min-h-screen bg-whiteYellow py-10 px-4 md:py-20">
          <div className="max-w-[700px] mx-auto my-10 text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              {useEnglish ? 'Inquiry Completed ' : 'お問い合わせ完了'}
            </h1>
            <p className="text-gray-700 mb-8">
              {useEnglish
                ? 'Your inquiry has been completed. Our representative will contact you within 4 to 5 business days.'
                : 'お問合わせが完了しました。担当者から4〜5営業日でご連絡いたします。'}
            </p>
            <div className="flex gap-4 justify-center">
              <LocalizedLink to="/hotel">
                <button className="bg-gray-500 text-white py-2 px-6 rounded hover:bg-gray-600 transition-colors max-md:text-xs">
                  {useEnglish ? 'Hotel Top Page' : '宿泊トップページ'}
                </button>
              </LocalizedLink>
              <LocalizedLink to="/monthly">
                <button className="bg-gray-500 text-white py-2 px-6 rounded hover:bg-gray-600 transition-colors max-md:text-xs">
                  {useEnglish ? 'Monthly Top Page' : 'マンスリートップページ'}
                </button>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};
